import { Fragment, useCallback, useLayoutEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, Button } from '@mui/material'
import { PageTitle } from '../components/atoms/PageTitle'
import { callAxios } from '../hooks/useAxios'
import { Group, User } from '../types/models'
import { usorter } from '../utils/sorter'
import { GroupDataTable } from '../components/GroupDataTable'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { UserDataTable } from '../components/UserDataTable'
import BackPageButton from '../components/atoms/BackPageButton'
import { Add } from '@mui/icons-material'
import { GroupActionsForm } from '../components/forms/GroupActionsForm'
import { GroupEmailSettings } from '../components/GroupEmailSettings'
import { DeleteGroupButton } from '../components/DeleteGroupButton'
import { RenameGroupButton } from '../components/RenameGroupButton'
import { useApp } from '../hooks/useApp'

export type ModalActionType = 'CREATE_GROUP' | 'CREATE_ADMIN' | 'CREATE_USER'

export function AdminUserGroupsPage(): React.ReactElement {

  const [currentGroup, setCurrentGroup] = useState<Group>({
    name: '',
  })
  const [groups, setGroups] = useState<Group[]>([])
  const [admins, setAdmins] = useState<any[]>([])
  const [users, setUsers] = useState<User[]>([])

  const [openUserGroupModal, setOpenUserGroupModal] = useState(false)
  const [modalType, setModalType] = useState<ModalActionType | ''>('')

  const { t } = useTranslation()
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const app = useApp()

  const getAllGroups = useCallback(() => {
    callAxios<Group[]>({
      url: '/user-groups',
    }).then(res => {
      if (res.success) {
        const root = res.data.filter(g => g.parent_group_id === null)
        if (app.user?.role !== 'ROOT' && root.length === 1) {
          navigate(`/admin/user-groups/${root[0].id}`)
        } else {
          setGroups(root)
        }
      } else {
        app.addError(res)
      }
    })
  }, [app, navigate])

  const updateUsers = useCallback(() => {
    callAxios<User[]>({
      url: `/user-groups/${params.id}/users`,
    }).then(res => {
      if (res.success) {
        const users = res.data.filter(user => user.role === 'READER')
        const admins = res.data.filter(user => user.role === 'ADMIN')
        setAdmins(usorter(admins, 'name'))
        setUsers(usorter(users, 'name'))
      } else {
        app.addError(res)
      }
    })
  }, [params, app])

  const updateSubgroups = useCallback(() => {
    callAxios<Group>({
      url: `/user-groups/${params.id}`,
    }).then(res => {
      if (res.success) {
        if (!res.data.editable && res.data.children && res.data.children.length === 1) {
          navigate(`/admin/user-groups/${res.data.children[0].id}`)
        } else {
          setCurrentGroup(res.data)
          setGroups(usorter(res.data.children as Group[], 'id'))
        }
      } else {
        app.addError(res)
      }
    })
  }, [params, app, navigate])

  const openGroupModal = (type: ModalActionType) => {
    setModalType(type)
    setOpenUserGroupModal(true)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (params.id) {
      updateSubgroups()
      updateUsers()
    } else {
      getAllGroups()
    }

    return () => {
      setGroups([])
      setAdmins([])
      setUsers([])
    }
  }, [getAllGroups, params, updateSubgroups, updateUsers, openUserGroupModal])

  return (
    <Fragment>
      {location.pathname !== '/admin/user-groups' &&
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 6 }}>
          <BackPageButton />
          <PageTitle sx={{ ml: 4 }} title={currentGroup?.name as string}/>
        </Box>}

      {location.pathname !== '/admin/user-groups' && currentGroup?.id &&
        <GroupEmailSettings group={currentGroup}/>}

      {location.pathname !== '/admin/user-groups' && <>
        {<Box sx={{ my: 5 }}>
          <Box>
            <PageTitle title={t('Users')}/>
            <Box sx={{ position: 'relative' }}>
              <UserDataTable searchbarID='users-search-bar' users={users || []}/>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button variant="outlined" color="secondary" size="large" onClick={() => openGroupModal('CREATE_USER')} endIcon={<Add/>}>
                <Trans>Create new user</Trans>
              </Button>
            </Box>
          </Box>
        </Box>}
        {<Box sx={{ my: 5 }}>
          <Box>
            <PageTitle title={t('Admins')}/>
            <Box sx={{ mb: 4, position: 'relative' }}>
              <UserDataTable searchbarID='admin-search-bar' users={admins || []}/>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button variant="outlined" color="secondary" size="large" onClick={() => openGroupModal('CREATE_ADMIN')} endIcon={<Add/>}>
                <Trans>Create new admin</Trans>
              </Button>
            </Box>
          </Box>
        </Box>}
      </>}

      <>
        <GroupDataTable groups={groups || []} openUserGroupModal={openUserGroupModal} setOpenUserGroupModal={setOpenUserGroupModal}/>
        {location.pathname !== '/admin/user-groups' && <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" color="secondary" size="large" onClick={() => openGroupModal('CREATE_GROUP')} endIcon={<Add/>}>
            <Trans>Create new group</Trans>
          </Button>
        </Box>}
        {location.pathname !== '/admin/user-groups' && currentGroup?.id && String(currentGroup.id) === params.id &&
          <RenameGroupButton group={currentGroup} onRenamed={name => setCurrentGroup(group => ({ ...group, name }))}/>}
        {location.pathname !== '/admin/user-groups' && currentGroup?.id && String(currentGroup.id) === params.id &&
          <DeleteGroupButton group={currentGroup}/>}
      </>

      <GroupActionsForm parentGroup={currentGroup} modalType={modalType || ''} setModalType={setModalType} openUserGroupModal={openUserGroupModal} setOpenUserGroupModal={setOpenUserGroupModal}/>
    </Fragment>
  )
}
